import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/common/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DatePicker } from '@/components/ui/date-picker';
import { SearchInput } from '@/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { format, subMonths } from 'date-fns';
import {
  Eye,
  FileText,
  Loader2,
  AlertCircle,
  Settings2,
  Users,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import api from '@/lib/api';
import { toISODate, useDateFormat } from '@/lib/dates';
import { ROUTES } from '@/constants/routes';
import { useStore } from '@/lib/store';
import { useHideAmounts } from '@/hooks/useHideAmounts';
import { UnifiedInvoiceAPI } from '@/services/invoicing';
import { INVOICE_DETAIL_LEVELS } from '@/types/invoicing';
import type { SplitPreview, AdjustmentLine } from '@/types/invoicing';
import { AdjustmentLinesEditor } from '@/components/invoices/unified-invoicing/AdjustmentLinesEditor';
import { formatCurrency as formatCurrencyBase } from '@/utils/document';
// Types for our data
interface Matter {
  id: number;
  name: string;
  group_name: string;
  uninvoiced_amount: number;
  uninvoiced_expense_amount: number;
  default_invoice_detail_level?: '' | 'short' | 'summary' | 'detailed';
}

interface Timesheet {
  id: number;
  matter: number;
  description: string;
  fee: number;
  user: number;
  date: string;
  billable_hours: number;
  billable: boolean;
  invoiced: boolean;
}

interface Expense {
  id: number;
  matter: number;
  description: string;
  amount: number;
  date: string;
  category: string;
  billable: boolean;
  invoiced: boolean;
}

interface MatterSelection {
  [matterId: number]: boolean;
}

interface ItemSelection {
  [matterId: number]: {
    [itemId: number]: boolean;
  };
}

// Per-matter invoice adjustments
interface MatterAdjustments {
  discount_type: 'none' | 'percentage' | 'fixed' | 'both';
  discount_percentage: string;
  discount_amount: string;
  discount_description: string;
  show_discount_on_invoice: boolean;
  flat_fee_adjustment: string;
  flat_fee_description: string;
  show_flat_fee_on_invoice: boolean;
  hour_adjustment: string;
  rate_adjustment: string;
  hour_adjustment_description: string;
  show_hour_adjustment_on_invoice: boolean;
  target_amount: string;
  fixed_deduction_amount: string;
  fixed_deduction_description: string;
  show_fixed_deduction_on_invoice: boolean;
  detail_level: '' | 'short' | 'summary' | 'detailed';
  adjustment_lines: AdjustmentLine[];
}

const defaultAdjustments: MatterAdjustments = {
  discount_type: 'none',
  discount_percentage: '',
  discount_amount: '',
  discount_description: '',
  show_discount_on_invoice: true,
  flat_fee_adjustment: '',
  flat_fee_description: '',
  show_flat_fee_on_invoice: true,
  hour_adjustment: '',
  rate_adjustment: '',
  hour_adjustment_description: '',
  show_hour_adjustment_on_invoice: true,
  target_amount: '',
  fixed_deduction_amount: '',
  fixed_deduction_description: '',
  show_fixed_deduction_on_invoice: true,
  detail_level: '',
  adjustment_lines: [],
};

export function InvoiceCreation() {
  const { users, fetchUsers } = useStore();
  const { hideAmounts } = useHideAmounts();
  const { format: formatDisplayDate } = useDateFormat();
  const [matters, setMatters] = useState<Matter[]>([]);
  const [timesheets, setTimesheets] = useState<{[matterId: number]: Timesheet[]}>({});
  const [expenses, setExpenses] = useState<{[matterId: number]: Expense[]}>({});
  const [selectedMatters, setSelectedMatters] = useState<MatterSelection>({});
  const [selectedTimesheets, setSelectedTimesheets] = useState<ItemSelection>({});
  const [selectedExpenses, setSelectedExpenses] = useState<ItemSelection>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMatter, setCurrentMatter] = useState<Matter | null>(null);
  const [timesheetSearchQuery, setTimesheetSearchQuery] = useState('');
  const [expenseSearchQuery, setExpenseSearchQuery] = useState('');
  const [currentTabView, setCurrentTabView] = useState<'timesheets' | 'expenses'>('timesheets');
  
  // Enhanced states
  const [sendEmailNotification, setSendEmailNotification] = useState(false);
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [invoicePerClient, setInvoicePerClient] = useState(false);
  
  // Per-matter adjustments (fetched from server, set on Timesheet Review page)
  const [serverAdjustments, setServerAdjustments] = useState<{[matterId: number]: MatterAdjustments}>({});
  // Global detail level (applied when per-matter is empty)
  const [globalDetailLevel, setGlobalDetailLevel] = useState<'' | 'short' | 'summary' | 'detailed'>('');
  
  // Split preview state
  const [splitPreviews, setSplitPreviews] = useState<{[matterId: number]: SplitPreview}>({});
  const [splitPreviewLoading, setSplitPreviewLoading] = useState<{[matterId: number]: boolean}>({});
  const [expandedSplitPreviews, setExpandedSplitPreviews] = useState<{[matterId: number]: boolean}>({});

  // Phase attribution for invoices now happens automatically by date
  // overlap against the matter's MatterPhase ranges. Users who want a
  // manual override can reassign individual timesheets to another phase
  // from the list view.
  
  // Date filtering state for timesheets
  const [dateFilter, setDateFilter] = useState<'all' | 'current-month' | 'last-month' | 'all-until-last-month' | 'custom'>('all-until-last-month');
  const [startDate, setStartDate] = useState<Date>(subMonths(new Date(), 1));
  const [endDate, setEndDate] = useState<Date>(new Date());
  
  // Date filtering state for expenses
  const [expenseDateFilter, setExpenseDateFilter] = useState<'all' | 'current-month' | 'last-month' | 'all-until-last-month' | 'custom'>('all-until-last-month');
  const [expenseStartDate, setExpenseStartDate] = useState<Date>(subMonths(new Date(), 1));
  const [expenseEndDate, setExpenseEndDate] = useState<Date>(new Date());

  const navigate = useNavigate();

  // Format currency, honouring the hide-amounts privacy toggle
  const formatCurrency = (amount: number | undefined) => {
    if (hideAmounts) return '•••';
    return formatCurrencyBase(amount);
  };

  const getUserName = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      return `${user.first_name} ${user.last_name}`;
    }
    return 'Unknown';
  };

  // Build URL for the timesheet review page, including current date filters
  const buildReviewUrl = (matterId: number) => {
    const params = new URLSearchParams();
    params.append('matter', matterId.toString());
    const today = new Date();
    if (dateFilter === 'current-month') {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      params.append('date_from', format(startOfMonth, 'yyyy-MM-dd'));
      params.append('date_to', format(today, 'yyyy-MM-dd'));
    } else if (dateFilter === 'last-month') {
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      params.append('date_from', format(lastMonth, 'yyyy-MM-dd'));
      params.append('date_to', format(new Date(today.getFullYear(), today.getMonth(), 0), 'yyyy-MM-dd'));
    } else if (dateFilter === 'all-until-last-month') {
      params.append('date_to', format(new Date(today.getFullYear(), today.getMonth(), 0), 'yyyy-MM-dd'));
    } else if (dateFilter === 'custom' && startDate && endDate) {
      params.append('date_from', format(startDate, 'yyyy-MM-dd'));
      params.append('date_to', format(endDate, 'yyyy-MM-dd'));
    }
    return `${ROUTES.TIMESHEET_REVIEW}?${params.toString()}`;
  };

  // Fetch data with date filters
  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Build date filter parameters for timesheets
      const timesheetParams = new URLSearchParams();
      
      if (dateFilter !== 'all') {
        const today = new Date();
        if (dateFilter === 'current-month') {
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          timesheetParams.append('start_date', format(startOfMonth, 'yyyy-MM-dd'));
          timesheetParams.append('end_date', format(today, 'yyyy-MM-dd'));
        } else if (dateFilter === 'last-month') {
          const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const startOfLastMonth = format(lastMonth, 'yyyy-MM-dd');
          const endOfLastMonth = format(new Date(today.getFullYear(), today.getMonth(), 0), 'yyyy-MM-dd');
          timesheetParams.append('start_date', startOfLastMonth);
          timesheetParams.append('end_date', endOfLastMonth);
        } else if (dateFilter === 'all-until-last-month') {
          const endOfLastMonth = format(new Date(today.getFullYear(), today.getMonth(), 0), 'yyyy-MM-dd');
          timesheetParams.append('end_date', endOfLastMonth);
        } else if (dateFilter === 'custom' && startDate && endDate) {
          timesheetParams.append('start_date', format(startDate, 'yyyy-MM-dd'));
          timesheetParams.append('end_date', format(endDate, 'yyyy-MM-dd'));
        }
      }
      
      timesheetParams.append('invoiced', 'false');
      timesheetParams.append('billable', 'true');

      // Build date filter parameters for expenses
      const expenseParams = new URLSearchParams();
      
      if (expenseDateFilter !== 'all') {
        const today = new Date();
        if (expenseDateFilter === 'current-month') {
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          expenseParams.append('start_date', format(startOfMonth, 'yyyy-MM-dd'));
          expenseParams.append('end_date', format(today, 'yyyy-MM-dd'));
        } else if (expenseDateFilter === 'last-month') {
          const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const startOfLastMonth = format(lastMonth, 'yyyy-MM-dd');
          const endOfLastMonth = format(new Date(today.getFullYear(), today.getMonth(), 0), 'yyyy-MM-dd');
          expenseParams.append('start_date', startOfLastMonth);
          expenseParams.append('end_date', endOfLastMonth);
        } else if (expenseDateFilter === 'all-until-last-month') {
          const endOfLastMonth = format(new Date(today.getFullYear(), today.getMonth(), 0), 'yyyy-MM-dd');
          expenseParams.append('end_date', endOfLastMonth);
        } else if (expenseDateFilter === 'custom' && expenseStartDate && expenseEndDate) {
          expenseParams.append('start_date', format(expenseStartDate, 'yyyy-MM-dd'));
          expenseParams.append('end_date', format(expenseEndDate, 'yyyy-MM-dd'));
        }
      }
      
      expenseParams.append('invoiced', 'false');
      
      // Fetch timesheets, expenses and matters in parallel — they are
      // independent requests
      const [timesheetsResponse, expensesResponse, mattersResponse] = await Promise.all([
        api.get(`${ROUTES.API.TIMESHEET}query/?limit=9999&${timesheetParams.toString()}`),
        api.get(`${ROUTES.API.COSTS}?limit=9999&invoiced=false&${expenseParams.toString()}`),
        api.get(`${ROUTES.API.MATTERS}?limit=9999`),
      ]);

      // Group timesheets by matter and calculate uninvoiced amounts
      const timesheetsByMatter: {[matterId: number]: Timesheet[]} = {};
      const matterAmounts: {[matterId: number]: number} = {};

      timesheetsResponse.data.results.forEach((timesheet: Timesheet) => {
        if (!timesheetsByMatter[timesheet.matter]) {
          timesheetsByMatter[timesheet.matter] = [];
          matterAmounts[timesheet.matter] = 0;
        }
        timesheetsByMatter[timesheet.matter].push(timesheet);
        matterAmounts[timesheet.matter] += parseFloat(String(timesheet.fee || 0));
      });

      // Group expenses by matter and calculate uninvoiced amounts
      const expensesByMatter: {[matterId: number]: Expense[]} = {};
      const expenseAmounts: {[matterId: number]: number} = {};
      
      expensesResponse.data.results.forEach((expense: Expense) => {
        if (!expensesByMatter[expense.matter]) {
          expensesByMatter[expense.matter] = [];
          expenseAmounts[expense.matter] = 0;
        }
        expensesByMatter[expense.matter].push(expense);
        expenseAmounts[expense.matter] += parseFloat(String(expense.amount || 0));
      });

      // Enhance matters with calculated uninvoiced amounts
      const enhancedMatters = mattersResponse.data.results
        .filter((matter: Matter) => 
          (timesheetsByMatter[matter.id]?.length > 0) || (expensesByMatter[matter.id]?.length > 0)
        )
        .map((matter: Matter) => ({
          ...matter,
          uninvoiced_amount: matterAmounts[matter.id] || 0,
          uninvoiced_expense_amount: expenseAmounts[matter.id] || 0
        }));
      
      setMatters(enhancedMatters);
      setTimesheets(timesheetsByMatter);
      setExpenses(expensesByMatter);
      
      // Reset selections when filters change
      setSelectedMatters({});
      setSelectedTimesheets({});
      setSelectedExpenses({});
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load uninvoiced matters, timesheets, and expenses',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch and when date filters change
  useEffect(() => {
    fetchData();
  }, [dateFilter, expenseDateFilter]);
  
  // Fetch adjustments from server after timesheets are loaded
  useEffect(() => {
    if (Object.keys(timesheets).length > 0) fetchAllAdjustments();
  }, [timesheets]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Trigger fetch when custom dates change (with debounce)
  useEffect(() => {
    if (dateFilter === 'custom' || expenseDateFilter === 'custom') {
      const timer = setTimeout(() => {
        fetchData();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [startDate, endDate, expenseStartDate, expenseEndDate, dateFilter, expenseDateFilter]);

  // Filter matters based on search query and sort by group name alphabetically
  const filteredMatters = useMemo(() => {
    return matters
      .filter(matter => 
        matter.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (matter.group_name && matter.group_name.toLowerCase().includes(searchQuery.toLowerCase()))
      )
      .sort((a, b) => {
        const groupA = a.group_name || '';
        const groupB = b.group_name || '';
        
        if (groupA === '' && groupB === '') {
          return a.name.localeCompare(b.name);
        }
        if (groupA === '') return 1;
        if (groupB === '') return -1;
        
        const groupComparison = groupA.localeCompare(groupB);
        if (groupComparison !== 0) {
          return groupComparison;
        }
        
        return a.name.localeCompare(b.name);
      });
  }, [matters, searchQuery]);

  // Filter timesheets for the current matter
  const filteredTimesheets = useMemo(() => {
    if (!currentMatter || !timesheets[currentMatter.id]) return [];
    
    return timesheets[currentMatter.id].filter(timesheet =>
      timesheet.description.toLowerCase().includes(timesheetSearchQuery.toLowerCase())
    );
  }, [currentMatter, timesheets, timesheetSearchQuery]);

  // Filter expenses for the current matter
  const filteredExpenses = useMemo(() => {
    if (!currentMatter || !expenses[currentMatter.id]) return [];
    
    return expenses[currentMatter.id].filter(expense =>
      expense.description.toLowerCase().includes(expenseSearchQuery.toLowerCase()) ||
      expense.category.toLowerCase().includes(expenseSearchQuery.toLowerCase())
    );
  }, [currentMatter, expenses, expenseSearchQuery]);

  // Calculate selected items for a matter
  const getSelectedItemsForMatter = (matterId: number) => {
    const selectedTimesheetIds = Object.keys(selectedTimesheets[matterId] || {})
      .filter(id => selectedTimesheets[matterId][parseInt(id)])
      .map(id => parseInt(id));
    
    const selectedExpenseIds = Object.keys(selectedExpenses[matterId] || {})
      .filter(id => selectedExpenses[matterId][parseInt(id)])
      .map(id => parseInt(id));

    return { timesheetIds: selectedTimesheetIds, expenseIds: selectedExpenseIds };
  };

  // Handle matter selection - Fixed to automatically select all items
  const handleMatterSelection = (matterId: number, selected: boolean) => {
    setSelectedMatters(prev => ({
      ...prev,
      [matterId]: selected
    }));

    // Auto-select/deselect all items for this matter
    if (selected) {
      // Select all timesheets for this matter
      const newSelectedTimesheets: { [itemId: number]: boolean } = {};
      (timesheets[matterId] || []).forEach(ts => {
        newSelectedTimesheets[ts.id] = true;
      });
      setSelectedTimesheets(prev => ({
        ...prev,
        [matterId]: newSelectedTimesheets
      }));

      // Select all expenses for this matter
      const newSelectedExpenses: { [itemId: number]: boolean } = {};
      (expenses[matterId] || []).forEach(exp => {
        newSelectedExpenses[exp.id] = true;
      });
      setSelectedExpenses(prev => ({
        ...prev,
        [matterId]: newSelectedExpenses
      }));
    } else {
      // Deselect all items for this matter
      setSelectedTimesheets(prev => ({
        ...prev,
        [matterId]: {}
      }));
      setSelectedExpenses(prev => ({
        ...prev,
        [matterId]: {}
      }));
    }
  };

  // Handle timesheet selection
  const handleTimesheetSelection = (matterId: number, timesheetId: number, selected: boolean) => {
    setSelectedTimesheets(prev => ({
      ...prev,
      [matterId]: {
        ...prev[matterId],
        [timesheetId]: selected
      }
    }));

    // Auto-select matter if any item is selected
    if (selected) {
      setSelectedMatters(prev => ({
        ...prev,
        [matterId]: true
      }));
    }
  };

  // Handle expense selection
  const handleExpenseSelection = (matterId: number, expenseId: number, selected: boolean) => {
    setSelectedExpenses(prev => ({
      ...prev,
      [matterId]: {
        ...prev[matterId],
        [expenseId]: selected
      }
    }));

    // Auto-select matter if any item is selected
    if (selected) {
      setSelectedMatters(prev => ({
        ...prev,
        [matterId]: true
      }));
    }
  };

  // Get adjustments for a matter (from server cache)
  const getAdjustments = useCallback((matterId: number): MatterAdjustments => {
    if (serverAdjustments[matterId]) return serverAdjustments[matterId];
    return defaultAdjustments;
  }, [serverAdjustments]);

  // Update adjustment_lines for a specific matter (local edit)
  const updateMatterAdjustmentLines = useCallback((matterId: number, lines: AdjustmentLine[]) => {
    setServerAdjustments(prev => ({
      ...prev,
      [matterId]: { ...(prev[matterId] || defaultAdjustments), adjustment_lines: lines },
    }));
  }, []);

  // Parse a server adjustments payload into local shape
  const parseAdjustments = (d: any): MatterAdjustments => ({
        discount_type: d.discount_type || 'none',
        discount_percentage: d.discount_percentage && Number(d.discount_percentage) !== 0 ? String(d.discount_percentage) : '',
        discount_amount: d.discount_amount && Number(d.discount_amount) !== 0 ? String(d.discount_amount) : '',
        discount_description: d.discount_description || '',
        show_discount_on_invoice: d.show_discount_on_invoice ?? true,
        flat_fee_adjustment: d.flat_fee_adjustment && Number(d.flat_fee_adjustment) !== 0 ? String(d.flat_fee_adjustment) : '',
        flat_fee_description: d.flat_fee_description || '',
        show_flat_fee_on_invoice: d.show_flat_fee_on_invoice ?? true,
        hour_adjustment: d.hour_adjustment && Number(d.hour_adjustment) !== 0 ? String(d.hour_adjustment) : '',
        rate_adjustment: d.rate_adjustment && Number(d.rate_adjustment) !== 0 ? String(d.rate_adjustment) : '',
        hour_adjustment_description: d.hour_adjustment_description || '',
        show_hour_adjustment_on_invoice: d.show_hour_adjustment_on_invoice ?? true,
        target_amount: d.target_amount && Number(d.target_amount) !== 0 ? String(d.target_amount) : '',
        fixed_deduction_amount: d.fixed_deduction_amount && Number(d.fixed_deduction_amount) !== 0 ? String(d.fixed_deduction_amount) : '',
        fixed_deduction_description: d.fixed_deduction_description || '',
        show_fixed_deduction_on_invoice: d.show_fixed_deduction_on_invoice ?? true,
        detail_level: d.detail_level || '',
        adjustment_lines: Array.isArray(d.adjustment_lines) ? d.adjustment_lines : [],
  });

  // Fetch adjustments for all matters that have timesheets loaded, in ONE
  // request (per-matter requests were an N+1 pattern: SENTRA-FRONTEND-A)
  const lastAdjustmentsFetchRef = useRef(0);
  const fetchAllAdjustments = useCallback(async () => {
    lastAdjustmentsFetchRef.current = Date.now();
    const matterIds = Object.keys(timesheets).map(Number);
    if (matterIds.length === 0) return;
    try {
      const res = await api.get(`/api/matters/bulk-invoice-adjustments/?ids=${matterIds.join(',')}`);
      const byMatter: Record<number, MatterAdjustments> = {};
      for (const [mId, d] of Object.entries(res.data)) {
        byMatter[Number(mId)] = parseAdjustments(d);
      }
      setServerAdjustments(prev => ({ ...prev, ...byMatter }));
    } catch { /* ignore — defaults will be used */ }
  }, [timesheets]);

  // Refresh adjustment data when window re-gains focus (user returns from
  // Timesheet Review page). Skip if fetched recently — refetching one
  // request per matter on every alt-tab hammers the API.
  useEffect(() => {
    const onFocus = () => {
      if (Date.now() - lastAdjustmentsFetchRef.current > 30_000) {
        fetchAllAdjustments();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchAllAdjustments]);

  // Check if a matter has any adjustments set (from server)
  const hasAdjustments = useCallback((matterId: number): boolean => {
    const adj = getAdjustments(matterId);
    return (
      adj.discount_type !== 'none' ||
      (adj.flat_fee_adjustment !== '' && adj.flat_fee_adjustment !== '0') ||
      (adj.hour_adjustment !== '' && adj.hour_adjustment !== '0') ||
      (adj.target_amount !== '' && adj.target_amount !== '0') ||
      (adj.fixed_deduction_amount !== '' && adj.fixed_deduction_amount !== '0') ||
      adj.detail_level !== '' ||
      (adj.adjustment_lines && adj.adjustment_lines.length > 0)
    );
  }, [getAdjustments]);

  // Calculate locally adjusted total for a matter (preview without API call)
  const getAdjustedTotal = useCallback((matterId: number): number => {
    const rawTotal = (timesheets[matterId] || []).reduce((sum, ts) => {
      const isSelected = selectedTimesheets[matterId]?.[ts.id];
      if (selectedTimesheets[matterId] && Object.keys(selectedTimesheets[matterId]).length > 0) {
        return isSelected ? sum + parseFloat(String(ts.fee || 0)) : sum;
      }
      return sum + parseFloat(String(ts.fee || 0));
    }, 0);
    const rawExpenses = (expenses[matterId] || []).reduce((sum, exp) => {
      const isSelected = selectedExpenses[matterId]?.[exp.id];
      if (selectedExpenses[matterId] && Object.keys(selectedExpenses[matterId]).length > 0) {
        return isSelected ? sum + parseFloat(String(exp.amount || 0)) : sum;
      }
      return sum + parseFloat(String(exp.amount || 0));
    }, 0);
    
    let total = rawTotal + rawExpenses;
    const adj = getAdjustments(matterId);
    
    // Apply discount
    if (adj.discount_type === 'percentage' && adj.discount_percentage) {
      total -= total * parseFloat(adj.discount_percentage) / 100;
    } else if (adj.discount_type === 'fixed' && adj.discount_amount) {
      total -= parseFloat(adj.discount_amount);
    } else if (adj.discount_type === 'both') {
      if (adj.discount_percentage) total -= total * parseFloat(adj.discount_percentage) / 100;
      if (adj.discount_amount) total -= parseFloat(adj.discount_amount);
    }
    if (adj.flat_fee_adjustment) total += parseFloat(adj.flat_fee_adjustment);
    if (adj.fixed_deduction_amount) total -= parseFloat(adj.fixed_deduction_amount);
    if (adj.target_amount && adj.target_amount !== '0') total = parseFloat(adj.target_amount);
    
    return Math.max(0, total);
  }, [timesheets, expenses, selectedTimesheets, selectedExpenses, getAdjustments]);

  // Build split preview request data for a matter
  const buildPreviewData = useCallback((matterId: number) => {
    const { timesheetIds, expenseIds } = getSelectedItemsForMatter(matterId);
    const allTs = timesheetIds.length > 0 ? timesheetIds : (timesheets[matterId] || []).map(ts => ts.id);
    const allExp = expenseIds.length > 0 ? expenseIds : (expenses[matterId] || []).map(exp => exp.id);
    if (allTs.length === 0 && allExp.length === 0) return null;
    
    const adj = getAdjustments(matterId);
    const detailLevel = adj.detail_level || globalDetailLevel;
    const data: any = {
      matter_id: matterId,
      timesheet_ids: allTs,
      cost_ids: allExp,
      issue_date: format(new Date(), 'yyyy-MM-dd'),
    };
    if (adj.discount_type !== 'none') {
      data.discount_type = adj.discount_type;
      if (adj.discount_percentage) data.discount_percentage = parseFloat(adj.discount_percentage);
      if (adj.discount_amount) data.discount_amount = parseFloat(adj.discount_amount);
    }
    if (adj.flat_fee_adjustment && adj.flat_fee_adjustment !== '0') data.flat_fee_adjustment = parseFloat(adj.flat_fee_adjustment);
    if (adj.hour_adjustment && adj.hour_adjustment !== '0') {
      data.hour_adjustment = parseFloat(adj.hour_adjustment);
      if (adj.rate_adjustment) data.rate_adjustment = parseFloat(adj.rate_adjustment);
    }
    if (adj.target_amount && adj.target_amount !== '0') data.target_amount = parseFloat(adj.target_amount);
    if (adj.fixed_deduction_amount && adj.fixed_deduction_amount !== '0') data.fixed_deduction_amount = parseFloat(adj.fixed_deduction_amount);
    if (detailLevel) data.detail_level = detailLevel;
    if (adj.adjustment_lines && adj.adjustment_lines.length > 0) {
      data.adjustment_lines = adj.adjustment_lines.map((line: AdjustmentLine, i: number) => ({
        line_type: line.line_type,
        description: line.description,
        amount: parseFloat(String(line.amount)) || 0,
        percentage: line.percentage ? parseFloat(String(line.percentage)) : null,
        show_on_invoice: line.show_on_invoice !== false,
        order: i,
      }));
    }
    return data;
  }, [timesheets, expenses, getAdjustments, globalDetailLevel]);

  // Fetch split preview for a matter
  const fetchSplitPreview = useCallback(async (matterId: number) => {
    const data = buildPreviewData(matterId);
    if (!data) return;
    
    setSplitPreviewLoading(prev => ({ ...prev, [matterId]: true }));
    try {
      const preview = await UnifiedInvoiceAPI.previewSplit(data);
      setSplitPreviews(prev => ({ ...prev, [matterId]: preview }));
    } catch (error) {
      console.error('Split preview failed:', error);
      toast({ title: 'Preview Error', description: 'Could not load fee-sharing preview', variant: 'destructive' });
    } finally {
      setSplitPreviewLoading(prev => ({ ...prev, [matterId]: false }));
    }
  }, [buildPreviewData]);

  // Toggle split preview for a matter (fetch on first expand)
  const toggleSplitPreview = useCallback((matterId: number) => {
    setExpandedSplitPreviews(prev => {
      const isExpanded = !prev[matterId];
      if (isExpanded) {
        fetchSplitPreview(matterId);
      }
      return { ...prev, [matterId]: isExpanded };
    });
  }, [fetchSplitPreview]);

  // Handle invoice creation - Simplified without management page
  const handleCreateInvoices = async () => {
    const selectedMatterIds = Object.keys(selectedMatters).filter(id => selectedMatters[parseInt(id)]);
    
    if (selectedMatterIds.length === 0) {
      toast({
        title: 'No Selection',
        description: 'Please select at least one matter to create invoices',
        variant: 'destructive'
      });
      return;
    }

    if (invoicePerClient) {
      // ----- Per-client merged path ---------------------------------
      setIsSubmitting(true);
      try {
        const mattersPayload = selectedMatterIds
          .map((matterIdStr) => {
            const matterId = parseInt(matterIdStr);
            const { timesheetIds, expenseIds } = getSelectedItemsForMatter(matterId);
            return {
              matter_id: matterId,
              timesheet_ids: timesheetIds,
              cost_ids: expenseIds,
            };
          })
          .filter((m) => m.timesheet_ids.length > 0 || m.cost_ids.length > 0);

        if (mattersPayload.length === 0) {
          toast({
            title: 'No items selected',
            description: 'Select at least one timesheet or expense to invoice.',
            variant: 'destructive',
          });
          return;
        }

        const created = await UnifiedInvoiceAPI.createPerClient({
          matters: mattersPayload,
          issue_date: format(new Date(), 'yyyy-MM-dd'),
          send_email_notification: sendEmailNotification,
          notes: invoiceNotes,
        });

        toast({
          title: 'Success',
          description: `${created.length} client invoice${created.length !== 1 ? 's' : ''} created, merging ${mattersPayload.length} matter${mattersPayload.length !== 1 ? 's' : ''}.`,
        });

        setSelectedMatters({});
        setSelectedTimesheets({});
        setSelectedExpenses({});
        setInvoiceNotes('');
        await fetchData();
        navigate(ROUTES.INVOICES);
      } catch (error: any) {
        console.error('Error creating per-client invoices:', error);
        toast({
          title: 'Error',
          description:
            error?.response?.data?.error ||
            error?.message ||
            'Failed to create per-client invoices',
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setIsSubmitting(true);
    let successCount = 0;
    let errorCount = 0;
    
    try {
      // Create one invoice per matter using the unified API
      await Promise.all(
        selectedMatterIds.map(async (matterIdStr) => {
          const matterId = parseInt(matterIdStr);
          const { timesheetIds, expenseIds } = getSelectedItemsForMatter(matterId);
          
          try {
            console.log(`Creating unified invoice for matter ${matterId}`);
            
            const invoiceData: any = {
              matter_id: matterId,
              timesheet_ids: timesheetIds,
              cost_ids: expenseIds,
              issue_date: format(new Date(), 'yyyy-MM-dd'),
              send_email_notification: sendEmailNotification,
              notes: invoiceNotes,
            };
            // No per-invoice phase picker — phase attribution is derived
            // from the invoice period's date range.

            // Add per-matter adjustments if any are set
            const adj = getAdjustments(matterId);
            if (adj.discount_type !== 'none') {
              invoiceData.discount_type = adj.discount_type;
              if ((adj.discount_type === 'percentage' || adj.discount_type === 'both') && adj.discount_percentage) {
                invoiceData.discount_percentage = parseFloat(adj.discount_percentage);
              }
              if ((adj.discount_type === 'fixed' || adj.discount_type === 'both') && adj.discount_amount) {
                invoiceData.discount_amount = parseFloat(adj.discount_amount);
              }
              invoiceData.discount_description = adj.discount_description;
              invoiceData.show_discount_on_invoice = adj.show_discount_on_invoice;
            }
            if (adj.flat_fee_adjustment && adj.flat_fee_adjustment !== '0') {
              invoiceData.flat_fee_adjustment = parseFloat(adj.flat_fee_adjustment);
              invoiceData.flat_fee_description = adj.flat_fee_description;
              invoiceData.show_flat_fee_on_invoice = adj.show_flat_fee_on_invoice;
            }
            if (adj.hour_adjustment && adj.hour_adjustment !== '0') {
              invoiceData.hour_adjustment = parseFloat(adj.hour_adjustment);
              if (adj.rate_adjustment) invoiceData.rate_adjustment = parseFloat(adj.rate_adjustment);
              invoiceData.hour_adjustment_description = adj.hour_adjustment_description;
              invoiceData.show_hour_adjustment_on_invoice = adj.show_hour_adjustment_on_invoice;
            }
            if (adj.target_amount && adj.target_amount !== '0') {
              invoiceData.target_amount = parseFloat(adj.target_amount);
            }
            if (adj.fixed_deduction_amount && adj.fixed_deduction_amount !== '0') {
              invoiceData.fixed_deduction_amount = parseFloat(adj.fixed_deduction_amount);
              invoiceData.fixed_deduction_description = adj.fixed_deduction_description;
              invoiceData.show_fixed_deduction_on_invoice = adj.show_fixed_deduction_on_invoice;
            }
            // Detail level: per-matter > global > server default
            const detailLevel = adj.detail_level || globalDetailLevel;
            if (detailLevel) {
              invoiceData.detail_level = detailLevel;
            }

            // Structured adjustment lines
            if (adj.adjustment_lines && adj.adjustment_lines.length > 0) {
              invoiceData.adjustment_lines = adj.adjustment_lines.map((line: AdjustmentLine, i: number) => ({
                line_type: line.line_type,
                description: line.description,
                amount: parseFloat(String(line.amount)) || 0,
                percentage: line.percentage ? parseFloat(String(line.percentage)) : null,
                show_on_invoice: line.show_on_invoice !== false,
                order: i,
              }));
            }

            await UnifiedInvoiceAPI.createFromTimesheets(invoiceData);
            successCount++;
          } catch (error) {
            console.error(`Error creating invoice for matter ${matterId}:`, error);
            errorCount++;
          }
        })
      );
      
      if (successCount > 0) {
        toast({
          title: 'Success',
          description: `${successCount} invoice${successCount > 1 ? 's' : ''} created successfully`,
        });

        // Reset selections
        setSelectedMatters({});
        setSelectedTimesheets({});
        setSelectedExpenses({});
        setInvoiceNotes('');
        
        // Refresh data to remove invoiced items
        await fetchData();
        
        // Navigate to invoices page
        navigate(ROUTES.INVOICES);
      }

      if (errorCount > 0 && successCount === 0) {
        toast({
          title: 'Error',
          description: 'Failed to create invoices',
          variant: 'destructive'
        });
      } else if (errorCount > 0) {
        toast({
          title: 'Partial Success',
          description: `${successCount} invoices created, ${errorCount} failed`,
        });
      }
      
    } catch (error) {
      console.error('Error creating invoices:', error);
      toast({
        title: 'Error',
        description: 'Failed to create invoices',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get status badge
  /*const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      draft: 'outline',
      sent: 'secondary',
      paid: 'default',
      cancelled: 'destructive'
    };

    return (
      <Badge variant={variants[status] || 'outline'}>
        {status.toUpperCase()}
      </Badge>
    );
  };*/

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Create Invoices</h1>
            <p className="text-muted-foreground">
              Select matters and their uninvoiced items to create invoices
            </p>
          </div>

          {/* Filters and Search */}
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
              <CardDescription>
                Filter matters by uninvoiced timesheets and expenses
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <SearchInput
                placeholder="Search matters..."
                value={searchQuery}
                onChange={setSearchQuery}
              />

              {/* Date Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Timesheet Date Filter */}
                <div className="space-y-2">
                  <Label>Timesheet Date Filter</Label>
                  <Select value={dateFilter} onValueChange={(value: any) => setDateFilter(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Timesheets</SelectItem>
                      <SelectItem value="current-month">Current Month</SelectItem>
                      <SelectItem value="last-month">Last Month</SelectItem>
                      <SelectItem value="all-until-last-month">All Until Last Month</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {dateFilter === 'custom' && (
                    <div className="flex gap-2">
                      <DatePicker
                        value={toISODate(startDate)}
                        onChange={(iso) => iso && setStartDate(new Date(iso))}
                        placeholder="Start date"
                        clearable={false}
                      />
                      <DatePicker
                        value={toISODate(endDate)}
                        onChange={(iso) => iso && setEndDate(new Date(iso))}
                        placeholder="End date"
                        clearable={false}
                      />
                    </div>
                  )}
                </div>

                {/* Expense Date Filter */}
                <div className="space-y-2">
                  <Label>Expense Date Filter</Label>
                  <Select value={expenseDateFilter} onValueChange={(value: any) => setExpenseDateFilter(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Expenses</SelectItem>
                      <SelectItem value="current-month">Current Month</SelectItem>
                      <SelectItem value="last-month">Last Month</SelectItem>
                      <SelectItem value="all-until-last-month">All Until Last Month</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {expenseDateFilter === 'custom' && (
                    <div className="flex gap-2">
                      <DatePicker
                        value={toISODate(expenseStartDate)}
                        onChange={(iso) => iso && setExpenseStartDate(new Date(iso))}
                        placeholder="Start date"
                        clearable={false}
                      />
                      <DatePicker
                        value={toISODate(expenseEndDate)}
                        onChange={(iso) => iso && setExpenseEndDate(new Date(iso))}
                        placeholder="End date"
                        clearable={false}
                      />
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Matters List */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>Matters with Uninvoiced Items</CardTitle>
                  <CardDescription>
                    {isLoading ? 'Loading...' : `${filteredMatters.length} matters found`}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="detail-level" className="text-sm whitespace-nowrap">Invoice Format</Label>
                    <Select value={globalDetailLevel || '__default__'} onValueChange={(v: any) => setGlobalDetailLevel(v === '__default__' ? '' : v)}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder="Firm default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default__">Firm Default</SelectItem>
                        {INVOICE_DETAIL_LEVELS.map(l => (
                          <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="send-email"
                      checked={sendEmailNotification}
                      onCheckedChange={setSendEmailNotification}
                    />
                    <Label htmlFor="send-email">Send email notifications</Label>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : filteredMatters.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No uninvoiced matters found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your date filters or check if there are any uninvoiced timesheets and expenses.
                  </p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={filteredMatters.length > 0 && filteredMatters.every(matter => selectedMatters[matter.id])}
                            onCheckedChange={(checked) => {
                              filteredMatters.forEach(matter => {
                                handleMatterSelection(matter.id, checked as boolean);
                              });
                            }}
                          />
                        </TableHead>
                        <TableHead>Matter</TableHead>
                        <TableHead>Group</TableHead>
                        <TableHead className="text-right">Raw Total</TableHead>
                        <TableHead className="text-right">Adjusted</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMatters.map((matter) => {
                        const { timesheetIds, expenseIds } = getSelectedItemsForMatter(matter.id);
                        const hasItems = (timesheets[matter.id]?.length || 0) + (expenses[matter.id]?.length || 0) > 0;
                        const rawTotal = matter.uninvoiced_amount + matter.uninvoiced_expense_amount;
                        const adjustedTotal = getAdjustedTotal(matter.id);
                        const isAdjusted = hasAdjustments(matter.id);
                        const isExpanded = expandedSplitPreviews[matter.id];
                        const splitPreview = splitPreviews[matter.id];
                        const splitLoading = splitPreviewLoading[matter.id];
                        
                        return (
                          <>
                            <TableRow key={matter.id} className={isExpanded ? 'border-b-0' : ''}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedMatters[matter.id] || false}
                                  onCheckedChange={(checked) => handleMatterSelection(matter.id, checked as boolean)}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="font-medium">{matter.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {timesheetIds.length + expenseIds.length} / {(timesheets[matter.id]?.length || 0) + (expenses[matter.id]?.length || 0)} items
                                  {matter.uninvoiced_amount > 0 && <span className="ml-2">Fees: {formatCurrency(matter.uninvoiced_amount)}</span>}
                                  {matter.uninvoiced_expense_amount > 0 && <span className="ml-2">Costs: {formatCurrency(matter.uninvoiced_expense_amount)}</span>}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {matter.group_name || 'No Group'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right text-muted-foreground">
                                {formatCurrency(rawTotal)}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="font-medium">
                                  {formatCurrency(adjustedTotal)}
                                </div>
                                {isAdjusted && Math.abs(adjustedTotal - rawTotal) > 0.01 && (
                                  <div className="text-xs text-orange-600">
                                    {adjustedTotal < rawTotal ? '−' : '+'}{formatCurrency(Math.abs(adjustedTotal - rawTotal))}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentMatter(matter)}
                                    disabled={!hasItems}
                                  >
                                    <Eye className="h-4 w-4 mr-1" />
                                    Items
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    asChild
                                  >
                                    <Link to={buildReviewUrl(matter.id)} target="_blank">
                                      <Settings2 className="h-4 w-4 mr-1" />
                                      Adjust
                                      {isAdjusted && (
                                        <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">!</Badge>
                                      )}
                                    </Link>
                                  </Button>
                                  <Button
                                    variant={isExpanded ? "secondary" : "outline"}
                                    size="sm"
                                    onClick={() => toggleSplitPreview(matter.id)}
                                    disabled={!hasItems}
                                    title="Preview fee-sharing split per client"
                                  >
                                    <Users className="h-4 w-4 mr-1" />
                                    Split
                                    {isExpanded ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                            {/* Split Preview Expandable Row */}
                            {isExpanded && (
                              <TableRow key={`${matter.id}-split`} className="bg-muted/30 hover:bg-muted/40">
                                <TableCell colSpan={6} className="p-0">
                                  <div className="px-6 py-4">
                                    {splitLoading ? (
                                      <div className="flex items-center justify-center py-4">
                                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                        <span className="text-sm text-muted-foreground">Loading split preview...</span>
                                      </div>
                                    ) : splitPreview ? (
                                      <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                          <h4 className="text-sm font-semibold">
                                            Fee-Sharing Split — {splitPreview.participants.length} client{splitPreview.participants.length !== 1 ? 's' : ''}
                                            {splitPreview.phase === 'primary' && (
                                              <Badge variant="outline" className="ml-2 text-[10px]">Fixed-amount phase</Badge>
                                            )}
                                          </h4>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => fetchSplitPreview(matter.id)}
                                            className="text-xs"
                                          >
                                            <Loader2 className={`h-3 w-3 mr-1 ${splitLoading ? 'animate-spin' : ''}`} />
                                            Refresh
                                          </Button>
                                        </div>
                                        {/* Summary row */}
                                        {splitPreview.discount_amount > 0 || splitPreview.flat_fee_adjustment !== 0 || splitPreview.fixed_deduction > 0 || splitPreview.standalone_deduction > 0 ? (
                                          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground border-b pb-2">
                                            <span>Raw: {formatCurrency(splitPreview.raw_total)}</span>
                                            {splitPreview.discount_amount > 0 && (
                                              <span className="text-red-600">Discount: −{formatCurrency(splitPreview.discount_amount)}</span>
                                            )}
                                            {splitPreview.flat_fee_adjustment !== 0 && (
                                              <span className={splitPreview.flat_fee_adjustment > 0 ? 'text-green-600' : 'text-red-600'}>
                                                Flat fee: {splitPreview.flat_fee_adjustment > 0 ? '+' : ''}{formatCurrency(splitPreview.flat_fee_adjustment)}
                                              </span>
                                            )}
                                            {splitPreview.hour_adjustment_amount !== 0 && (
                                              <span className={splitPreview.hour_adjustment_amount > 0 ? 'text-green-600' : 'text-red-600'}>
                                                Hour adj: {splitPreview.hour_adjustment_amount > 0 ? '+' : ''}{formatCurrency(splitPreview.hour_adjustment_amount)}
                                              </span>
                                            )}
                                            {splitPreview.fixed_deduction > 0 && (
                                              <span className="text-red-600">Deduction: −{formatCurrency(splitPreview.fixed_deduction)}</span>
                                            )}
                                            {splitPreview.standalone_deduction > 0 && (
                                              <span className="text-red-600">Prepaid: −{formatCurrency(splitPreview.standalone_deduction)}</span>
                                            )}
                                            <span className="font-medium text-foreground">Adjusted: {formatCurrency(splitPreview.adjusted_total)}</span>
                                          </div>
                                        ) : null}
                                        {/* Per-client breakdown table */}
                                        <div className="rounded-md border">
                                          <Table>
                                            <TableHeader>
                                              <TableRow className="text-xs">
                                                <TableHead className="py-2">Client</TableHead>
                                                <TableHead className="py-2 text-right">Share</TableHead>
                                                <TableHead className="py-2 text-right">Subtotal</TableHead>
                                                <TableHead className="py-2 text-right">VAT</TableHead>
                                                <TableHead className="py-2 text-right font-semibold">Total</TableHead>
                                                <TableHead className="py-2">Format</TableHead>
                                              </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                              {splitPreview.participants.map((p) => (
                                                <TableRow key={p.client_id} className="text-xs">
                                                  <TableCell className="py-2">
                                                    <div className="font-medium">{p.client_name}</div>
                                                    {p.fee_sharing_type === 'fixed_amount' && (
                                                      <div className="text-[10px] text-muted-foreground">
                                                        Fixed: {formatCurrency(p.fixed_amount ?? 0)} 
                                                        {p.fixed_amount_remaining !== null && ` (${formatCurrency(p.fixed_amount_remaining)} left)`}
                                                        {p.is_exhausted && <Badge variant="destructive" className="ml-1 text-[9px] px-1">Exhausted</Badge>}
                                                      </div>
                                                    )}
                                                  </TableCell>
                                                  <TableCell className="py-2 text-right">
                                                    {p.percentage_share.toFixed(1)}%
                                                  </TableCell>
                                                  <TableCell className="py-2 text-right">
                                                    {formatCurrency(p.subtotal)}
                                                  </TableCell>
                                                  <TableCell className="py-2 text-right">
                                                    {p.vat_amount > 0 ? (
                                                      <span>{formatCurrency(p.vat_amount)} ({p.vat_rate}%)</span>
                                                    ) : (
                                                      <span className="text-muted-foreground">{p.vat_type}</span>
                                                    )}
                                                  </TableCell>
                                                  <TableCell className="py-2 text-right font-semibold">
                                                    {formatCurrency(p.total)}
                                                  </TableCell>
                                                  <TableCell className="py-2">
                                                    <Badge variant="outline" className="text-[10px]">
                                                      {p.detail_level}
                                                      {p.invoice_detail_level_override && (
                                                        <span className="ml-1 text-primary">★</span>
                                                      )}
                                                    </Badge>
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </div>
                                        {/* Per-matter adjustment lines editor */}
                                        <div className="pt-2 border-t">
                                          <AdjustmentLinesEditor
                                            lines={getAdjustments(matter.id).adjustment_lines}
                                            onChange={(lines) => updateMatterAdjustmentLines(matter.id, lines)}
                                          />
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-sm text-muted-foreground text-center py-4">
                                        No split preview available. Select items first.
                                      </p>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })}
                    </TableBody>
                  </Table>

                  <div className="mt-6 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="invoice-notes">Invoice Notes (optional)</Label>
                      <Textarea
                        id="invoice-notes"
                        placeholder="Add any notes for all invoices..."
                        value={invoiceNotes}
                        onChange={(e) => setInvoiceNotes(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <div className="flex items-center justify-between border rounded-md p-3 bg-muted/30">
                      <div className="space-y-0.5">
                        <Label htmlFor="invoice-per-client" className="text-sm font-medium">
                          Create invoice per client
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          When enabled, all selected matters belonging to the
                          same client are merged into a single invoice for
                          that client (one invoice per client, not per
                          matter). Per-matter adjustments are ignored in this
                          mode.
                        </p>
                      </div>
                      <Switch
                        id="invoice-per-client"
                        checked={invoicePerClient}
                        onCheckedChange={setInvoicePerClient}
                      />
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="text-sm text-muted-foreground">
                        {Object.values(selectedMatters).filter(Boolean).length} matters selected
                        {invoicePerClient && ' — will be merged per client'}
                      </div>
                      <Button
                        onClick={handleCreateInvoices}
                        disabled={Object.values(selectedMatters).filter(Boolean).length === 0 || isSubmitting}
                        size="lg"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Creating Invoices...
                          </>
                        ) : invoicePerClient ? (
                          <>
                            <FileText className="h-4 w-4 mr-2" />
                            Create Invoice{Object.values(selectedMatters).filter(Boolean).length !== 1 ? 's' : ''} per Client
                          </>
                        ) : (
                          <>
                            <FileText className="h-4 w-4 mr-2" />
                            Create {Object.values(selectedMatters).filter(Boolean).length} Invoice{Object.values(selectedMatters).filter(Boolean).length !== 1 ? 's' : ''}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Matter Items Dialog */}
        <Dialog open={!!currentMatter} onOpenChange={() => setCurrentMatter(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>
                {currentMatter?.name} - Select Items to Invoice
              </DialogTitle>
            </DialogHeader>
            
            {currentMatter && (
              <Tabs value={currentTabView} onValueChange={(value: any) => setCurrentTabView(value)}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="timesheets">
                    Timesheets ({timesheets[currentMatter.id]?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="expenses">
                    Expenses ({expenses[currentMatter.id]?.length || 0})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="timesheets" className="mt-4">
                  <div className="space-y-4">
                    <SearchInput
                      placeholder="Search timesheets..."
                      value={timesheetSearchQuery}
                      onChange={setTimesheetSearchQuery}
                    />
                    
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <Checkbox
                                checked={filteredTimesheets.length > 0 && filteredTimesheets.every(ts => 
                                  selectedTimesheets[currentMatter.id]?.[ts.id]
                                )}
                                onCheckedChange={(checked) => {
                                  const newSelection = { ...selectedTimesheets };
                                  if (!newSelection[currentMatter.id]) {
                                    newSelection[currentMatter.id] = {};
                                  }
                                  
                                  filteredTimesheets.forEach(timesheet => {
                                    newSelection[currentMatter.id][timesheet.id] = checked as boolean;
                                  });
                                  
                                  setSelectedTimesheets(newSelection);
                                  
                                  if (checked) {
                                    setSelectedMatters(prev => ({
                                      ...prev,
                                      [currentMatter.id]: true
                                    }));
                                  }
                                }}
                              />
                            </TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Hours</TableHead>
                            <TableHead>User</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredTimesheets.map((timesheet) => (
                            <TableRow key={timesheet.id}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedTimesheets[currentMatter.id]?.[timesheet.id] || false}
                                  onCheckedChange={(checked) => 
                                    handleTimesheetSelection(currentMatter.id, timesheet.id, checked as boolean)
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                {formatDisplayDate(timesheet.date)}
                              </TableCell>
                              <TableCell>
                                <div className="max-w-xs truncate">{timesheet.description}</div>
                              </TableCell>
                              <TableCell>{timesheet.billable_hours}</TableCell>
                              <TableCell>{getUserName(timesheet.user)}</TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(timesheet.fee)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </div>
                </TabsContent>

                <TabsContent value="expenses" className="mt-4">
                  <div className="space-y-4">
                    <SearchInput
                      placeholder="Search expenses..."
                      value={expenseSearchQuery}
                      onChange={setExpenseSearchQuery}
                    />
                    
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <Checkbox
                                checked={filteredExpenses.length > 0 && filteredExpenses.every(exp => 
                                  selectedExpenses[currentMatter.id]?.[exp.id]
                                )}
                                onCheckedChange={(checked) => {
                                  const newSelection = { ...selectedExpenses };
                                  if (!newSelection[currentMatter.id]) {
                                    newSelection[currentMatter.id] = {};
                                  }
                                  
                                  filteredExpenses.forEach(expense => {
                                    newSelection[currentMatter.id][expense.id] = checked as boolean;
                                  });
                                  
                                  setSelectedExpenses(newSelection);
                                  
                                  if (checked) {
                                    setSelectedMatters(prev => ({
                                      ...prev,
                                      [currentMatter.id]: true
                                    }));
                                  }
                                }}
                              />
                            </TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredExpenses.map((expense) => (
                            <TableRow key={expense.id}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedExpenses[currentMatter.id]?.[expense.id] || false}
                                  onCheckedChange={(checked) => 
                                    handleExpenseSelection(currentMatter.id, expense.id, checked as boolean)
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                {formatDisplayDate(expense.date)}
                              </TableCell>
                              <TableCell>
                                <div className="max-w-xs truncate">{expense.description}</div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{expense.category}</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(expense.amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </div>
                </TabsContent>
              </Tabs>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCurrentMatter(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Adjustments Dialog removed — adjustments are now managed on the Timesheet Review page */}
      </div>
    </Layout>
  );
}